import {Component, HostListener, OnInit} from '@angular/core';
import {ActivatedRoute, Router, UrlSegment} from '@angular/router';
import {MessageService, TreeNode} from 'primeng/api';
import {translate, TranslocoService} from '@ngneat/transloco';
import {User, UserProfile} from '../../../model/user';
import {UsersService} from '../../../services/users.service';
import {ComponentCanDeactivate} from "../../../common/can-deactivate/component-can-deactivate";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {noSpecialCharacters, noWhitespaces} from "../../../shared/custom-validators";
import {CountryISO, PhoneNumberFormat, SearchCountryField} from 'ngx-intl-tel-input';
import * as moment from 'moment-timezone';
import {HierarchyService} from "../../../services/hierarchy.service";
import {PasswordService} from "../../../services/password.service";
import {PhoneService} from "../../../services/phone.service";
import {UserPersistRequest} from "../../../model/requests/user-persist-request";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {LoggedUser} from "../../../common/logged-user.service";
import {ProfilesService} from "../../../services/profiles.service";
import {AppConfig} from "../../../shared/global-variables";
import {CommonTranslationsService} from "../../../services/common-translations.service";

@Component({
  selector: 'app-users-form',
  templateUrl: './users-form.component.html',
  styleUrls: ['./users-form.component.scss']
})
export class UsersFormComponent implements OnInit, ComponentCanDeactivate {

  public mode: 'create' | 'edit';

  public SearchCountryField = SearchCountryField;
  public PhoneNumberFormat = PhoneNumberFormat;

  public timeZoneOptions: any[];
  public languageOptions: any[];
  public selectedLanguage: any;
  public preferredCountries: any[];

  public hierarchy: TreeNode[];
  public profiles: UserProfile[];
  public selectedOrganization: any;

  public requiredUserActions;

  public userForm: FormGroup;
  public persistingChanges: boolean;
  public userFromOtherHierarchy: boolean;

  private static readonly DEFAULT_TIME_ZONE: string = 'Europe/Madrid';
  private static readonly DEFAULT_LANGUAGE: string = 'es';

  private nodes: any[];
  private user: User;
  private formSuccess: boolean;


  constructor(private usersService: UsersService,
              private route: ActivatedRoute,
              private router: Router,
              private formBuilder: FormBuilder,
              private commonMessagesService: CommonMessagesService,
              private i18n: TranslocoService,
              private hierarchyService: HierarchyService,
              private messageService: MessageService,
              private passwordService: PasswordService,
              private phoneService: PhoneService,
              private loggedUser: LoggedUser,
              private profilesService: ProfilesService,
              private commonTranslationsService: CommonTranslationsService) {
    this.preferredCountries = [CountryISO.Spain];
    this.languageOptions = this.commonTranslationsService.getLanguageOptions();
    this.timeZoneOptions = moment.tz.names().map(zoneName => ({name: zoneName}));
    this.userFromOtherHierarchy = false;
  }

  public isUserLoading: boolean = true;

  ngOnInit(): void {
    this.i18n.selectTranslate('common.new').subscribe(() => {
      this.requiredUserActions = [
        {name: translate('misc.userActions.updatePassword'), code: 'UPDATE_PASSWORD', icon: 'pi pi-lock'},
        {name: translate('misc.userActions.updateProfile'), code: 'UPDATE_PROFILE', icon: 'pi pi-user'},
        {name: translate('misc.userActions.configureOtp'), code: 'CONFIGURE_TOTP', icon: 'pi pi-cog'},
        {name: translate('misc.userActions.verifyEmail'), code: 'VERIFY_EMAIL', icon: 'pi pi-envelope'}
      ];
    });

    this.loadDataAndForm();
  }

  private loadDataAndForm(): void {
    this.isUserLoading = true;
    const promises = [
      this.profilesService.getProfilesSummary().toPromise().then(response => {
        this.profiles = response.profiles;
      }),
      this.hierarchyService.getHierarchyDropdown().toPromise().then(response => {
        let responseTreeNode = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto);
        this.nodes = responseTreeNode[0];
        this.hierarchy = responseTreeNode[1];
      })
    ];

    Promise.all(promises).then(() => {
      this.route.url.subscribe(urlSegment => {
        const firstPath = urlSegment[0].path;
        if (firstPath === 'new') {
          this.mode = 'create';
          this.loadCreationForm();
        } else {
          this.mode = 'edit';
          this.loadEditionForm(urlSegment);
        }
      });
    }).finally(() => this.isUserLoading = false);
  }

  loadCreationForm(): void {
    this.user = new User();
    this.user.blocked = false;
    this.user.enabled = true;
    this.user.emailVerified = false;
    this.user.requieredActions = [];
    this.user.superadminApplications = [];
    this.user.adminApplications = ["ZMXCONNECT"];
    this.user.viewerApplications = [];
    this.user.language = UsersFormComponent.DEFAULT_LANGUAGE;
    this.selectLanguage(this.user);
    this.user.timeZone = UsersFormComponent.DEFAULT_TIME_ZONE;
    this.loggedUser.userObservable.subscribe(user => {
      this.user.tenantUUID = user.tenantUuid;
      this.user.organizationUUID = user.organizationUuid;
    });

    this.createForm(this.user);
  }

  loadEditionForm(urlSegment: UrlSegment[]): void {
    this.usersService.getUser(urlSegment[0].path).toPromise()
      .then(response => {
        this.user = response.user;
        this.selectedOrganization = this.nodes[this.user.organization.organizationUuid];
        if (this.selectedOrganization == null) {
          this.userFromOtherHierarchy = true;
          this.hierarchy = [];
          this.selectedOrganization = {
            label: this.user.organization.name,
            data: this.user.organization.organizationUuid
          }
        }
        this.user.phone = this.phoneService.parsePhoneNumber(this.user.phone);
        this.selectLanguage(this.user);
        this.createForm(this.user);
      }, error => {
        if (error.status === 404) {
          this.router.navigate(['/not-found']);
        }
      });
  }

  private selectLanguage(user: User): void {
    this.selectedLanguage = this.languageOptions.find(lang => lang.langCode === user.language);
  }

  private createForm(user: User): void {
    this.userForm = this.formBuilder.group({
      firstName: [user.firstName, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      lastName: [user.lastName, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      username: [user.username, [Validators.required, Validators.maxLength(255), noWhitespaces,
        Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH)]],
      email: [user.email, [Validators.maxLength(255), Validators.required, Validators.email]],
      phone: [user.phone, [Validators.maxLength(255), Validators.required]],
      profile: [user.profile?.profileUuid, [Validators.required]],
      eCommerceUser: [user.eCommerceUser, []],
      enabled: [user.enabled, [Validators.required]],
      blocked: [user.blocked, [Validators.required]],
      emailVerified: [user.emailVerified, [Validators.required]],
      requieredActions: [user.requieredActions],
      timeZone: [user.timeZone, [Validators.required]],
      password: ['', []],
      confirmPassword: ['', []],
      userUuid: this.mode === 'edit' ? this.user.userUuid : null
    });
    if (this.mode === 'edit') {
      this.userForm.get('username').disable();
    }
    this.userForm.markAsPending();
  }

  persistUserData(): void {
    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.usersService.createUser(persistData).toPromise();
    } else {
      promise = this.usersService.editUser(persistData).toPromise();
    }

    promise.then(() => {
      if (this.mode === 'create') {
        this.commonMessagesService.showSuccessfullyCreatedMessage();
      } else {
        this.commonMessagesService.showSuccessfullyUpdatedMessage();
      }
      this.formSuccess = true;
      this.router.navigate(['/users']);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.username);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): UserPersistRequest {
    const formValue = this.userForm.getRawValue();
    return {
      tenantUUID: this.user.tenantUUID,
      organizationUUID: this.user.organizationUUID,
      firstName: formValue.firstName?.trim(),
      lastName: formValue.lastName?.trim(),
      username: formValue.username?.trim(),
      email: formValue.email?.trim(),
      emailVerified: formValue.emailVerified,
      enabled: formValue.enabled,
      blocked: formValue.blocked,
      requieredActions: formValue.requieredActions,
      language: this.selectedLanguage.langCode,
      timeZone: formValue.timeZone,
      password: formValue.password,
      phone: formValue.phone ? formValue.phone.e164Number?.trim() : null,
      applicationOrganizationUuid: this.selectedOrganization.data,
      profileUuid: formValue.profile,
      superadminApplications: this.user.superadminApplications,
      adminApplications: this.user.adminApplications,
      viewerApplications: this.user.viewerApplications,
      uuid: this.user.userUuid
    };
  }

  public newPasswordCheck(): void {
    this.passwordService.newPasswordCheck(this.userForm);
  }

  public generatePassword(): void {
    this.passwordService.generateAndSetPassword(this.userForm);
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || (this.userForm?.untouched && this.organizationUntouched());
  }

  private organizationUntouched(): boolean {
    if (this.mode === 'create') {
      return this.selectedOrganization == null;
    } else {
      return this.selectedOrganization?.data === this.user.organization?.organizationUuid;
    }
  }

}
